import type { Theme, SxProps } from '@mui/material/styles';

import { m } from 'framer-motion';
import { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';

import { ROLES } from 'src/lib/consts';
import { ForbiddenIllustration } from 'src/assets/illustrations';

import { varBounce, MotionContainer } from 'src/components/animate';

import { useAuthContext } from '../hooks';

import type { UserType } from '../types';

// ----------------------------------------------------------------------

export type RoleBasedGuardProp = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

const userCanAccessInsights = (user: UserType) =>
  user?.role === ROLES.INSIGHTS || user?.role === ROLES.SUPER_ADMIN || user?.insights_access;

export function RoleBasedGuard({ sx, children }: RoleBasedGuardProp) {
  const location = useLocation();
  const { user, authenticated } = useAuthContext();
  const router = useRouter();

  const userHasAccessToThisPage = useMemo(() => {
    if (user && user.role === ROLES.SUPER_ADMIN) {
      return true;
    }

    if (user && user.role !== ROLES.SUPER_ADMIN) {
      if (location.pathname.includes('insights') && userCanAccessInsights(user)) {
        return true;
      }
    }

    return false;
  }, [user, location.pathname]);

  // if user is not super admin and has insighs access, redirect to insights
  useEffect(() => {
    if (
      user?.role !== ROLES.SUPER_ADMIN &&
      !location.pathname.includes('insights') &&
      userCanAccessInsights(user)
    ) {
      router.push('/insights');
    }
  }, [user, location, router]);

  if (!authenticated) {
    return <> {children} </>;
  }

  return !userHasAccessToThisPage ? (
    <Container
      component={MotionContainer}
      sx={[{ textAlign: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <m.div variants={varBounce('in')}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Permission denied
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <Typography sx={{ color: 'text.secondary' }}>
          You do not have permission to access this page.
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <ForbiddenIllustration sx={{ my: { xs: 5, sm: 10 } }} />
      </m.div>
    </Container>
  ) : (
    <> {children} </>
  );
}
